import { useLocation, useParams } from "react-router";
import qs from "qs";
import { useEffect } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box, Flex } from "rebass/styled-components";
import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getCurrentEvent } from "core-integration/src/store/events/reducers";
import {
  getH2HLeague,
  getH2HStandings,
} from "core-integration/src/store/leagues/reducers";
import { fetchH2HLeagueStandings } from "core-integration/src/store/leagues/thunks";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import ButtonLink from "../ButtonLink";
import { Main, Secondary, Wrapper } from "../Layout";
import Link from "../Link";
import { Pager, PagerItem, PagerItemNext } from "../Pager";
import Table, { ScrollTable } from "../Table";
import Tooltip, { TooltipLabel } from "../Tooltip";
import { ControlArrowLeft, ControlArrowRight } from "../icons/Arrows";
import H2HNav from "./H2HNav";
import Movement from "./Movement";
import StandingsHeading from "./StandingsHeading";
import StandingsRow from "./StandingsRow";

type Props = WithTranslation;

const StandingsH2H = ({ t }: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const location = useLocation();
  const { leagueId } = useParams();

  // Get league id from path
  const leagueNumber = parseInt(leagueId!, 10) || 0;

  // Get page numbers from query string
  const query = qs.parse(location!.search, {
    ignoreQueryPrefix: true,
  });

  const defaultEntryNumber = parseInt(query.default_entry as string, 10) || 0;
  const pageNewEntries = parseInt(query.page_new_entries as string, 10) || 1;
  const pageStandings = parseInt(query.page_standings as string, 10) || 1;

  const league = useSelector((state: RootState) =>
    getH2HLeague(state, leagueNumber),
  );

  const player = useSelector(getPlayerData);
  const standings = useSelector((state: RootState) =>
    getH2HStandings(state, leagueNumber, pageStandings),
  );

  const now = useSelector(getCurrentEvent);

  useEffect(() => {
    if (leagueNumber) {
      dispatch(
        fetchH2HLeagueStandings(leagueNumber, pageNewEntries, pageStandings),
      );
    }
  }, [dispatch, leagueNumber, pageNewEntries, pageStandings]);

  const buildLink = (pageStandings: number) => {
    return `/leagues/${league!.id}/standings/h?page_standings=${pageStandings}`;
  };

  if (!league) {
    return null;
  }
  return (
    <Wrapper>
      <Main>
        <StandingsHeading
          type="h2h"
          league={league}
          leagueNumber={leagueNumber}
        />
        {standings && standings.results[0] && now && (
          <H2HNav
            eventId={now.id}
            leagueId={league.id}
            defaultEntry={defaultEntryNumber && defaultEntryNumber}
          />
        )}
        <Box mb={2}>
          <ScrollTable>
            <Table>
              <thead>
                <tr>
                  <th scope="col">{t("standingH2h.rank", "Rank")}</th>
                  <th scope="col">{t("standingH2h.team", "Team & Manager")}</th>
                  <th scope="col">
                    <Tooltip content="Won">
                      <TooltipLabel>{t("standingH2h.win", "W")}</TooltipLabel>
                    </Tooltip>
                  </th>
                  <th scope="col">
                    <Tooltip content="Drew">
                      <TooltipLabel>{t("standingH2h.draw", "D")}</TooltipLabel>
                    </Tooltip>
                  </th>
                  <th scope="col">
                    <Tooltip content="Lost">
                      <TooltipLabel>{t("standingH2h.loose", "L")}</TooltipLabel>
                    </Tooltip>
                  </th>
                  <th scope="col">{t("standingH2h.score", "Score")}</th>
                  <th scope="col">
                    <Tooltip content="Points">
                      <TooltipLabel>{t("standingH2h.pts", "Pts")}</TooltipLabel>
                    </Tooltip>
                  </th>
                </tr>
              </thead>
              <tbody>
                {standings &&
                  standings.results.map((ls) => (
                    <StandingsRow
                      mine={
                        player !== null &&
                        player.entry !== null &&
                        player.entry === ls.entry
                      }
                      key={ls.entry}
                    >
                      <td>
                        <Flex alignItems="center">
                          <Box mr={1}>{ls.rank}</Box>
                          <Movement lastRank={ls.last_rank} rank={ls.rank} />
                        </Flex>
                      </td>
                      <td>
                        {ls.entry && now ? (
                          <Link to={`/entry/${ls.entry}/event/${now.id}`}>
                            <strong>{ls.entry_name}</strong>
                          </Link>
                        ) : (
                          <strong>{ls.entry_name}</strong>
                        )}
                        <br />
                        {ls.player_name}
                      </td>
                      <td>{ls.matches_won}</td>
                      <td>{ls.matches_drawn}</td>
                      <td>{ls.matches_lost}</td>
                      <td>{ls.points_for}</td>
                      <td>{ls.total}</td>
                    </StandingsRow>
                  ))}
              </tbody>
            </Table>
          </ScrollTable>
        </Box>
        {standings && standings.results.length > 0 && (
          <Box mb={4}>
            <Pager>
              <PagerItem>
                {pageStandings > 1 && (
                  <ButtonLink
                    to={buildLink(pageStandings - 1)}
                    variant="secondary"
                  >
                    <ControlArrowLeft />
                    <span>{t("standingH2h.previews", "Previous")}</span>
                  </ButtonLink>
                )}
              </PagerItem>
              <PagerItemNext>
                {standings.has_next && (
                  <ButtonLink
                    to={buildLink(pageStandings + 1)}
                    variant="secondary"
                  >
                    <span>{t("standingH2h.next", "Next")}</span>
                    <ControlArrowRight />
                  </ButtonLink>
                )}
              </PagerItemNext>
            </Pager>
          </Box>
        )}
      </Main>
      <Secondary>
        <></>
      </Secondary>
    </Wrapper>
  );
};

export default withTranslation()(StandingsH2H);
